/**
 * 确认订单页（按原型图精确还原）
 * 达人详情 → 点击"去下单" → 跳转到此页面
 * 核心接口:
 *   - POST /api/massage/order/calculate  价格计算（实时）
 *   - POST /api/massage/order/create     创建订单（提交时）
 *   - POST /api/massage/cashier         获取收银台（提交时）
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { peiwantHttp } from "@/http/axios";
import type { StaffDetail, StaffServiceItem } from "@/api/guide";
import { calculateOrderPrice, getCashier, type CalculatePriceData } from "@/api/expert-order";
import ExpertTimeComp from "./components/ExpertTimeComp";
import type { TimeConfirmParams } from "./types";

const PRIMARY = "#FF6B35";

/** 快捷备注标签 */
const QUICK_TAGS = ["可提前来", "来之前打电话", "准时到达", "着装整洁"];

/** order/create 请求参数 */
interface CreateOrderParams {
  book_time: string;
  book_end_time: string;
  staff_id: string | number;
  pay_type: string;
  item_id: string | number;
  num: string | number;
  coupon_id: string | number;
  remark: string;
  user_address_id: string | number;
  travel_mode_id: string | number;
  accept_order_type: string | number;
}

/** order/create 响应中的订单详情字段（用于页面展示） */
interface OrderData {
  id: number;
  ordersn: string;
  item_name: string;
  item_thumb: string;
  unit_price: string;
  unit_name: string;
  num: number;
  original_price: string;
  coupon_price: string;
  discount_price: string;
  pay_price: string;
  status: number;
}

export default function ExpertConfirmOrderPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const stateData = (location.state as {
    staffId?: number;
    itemId?: number;
    selectedCity?: string;
    selectedLat?: string;
    selectedLng?: string;
    selectedTownship?: string;
    /** 从地址选择页返回的已选地址ID */
    selectedAddressId?: number;
    /** 详细地址文本 */
    selectedDetail?: string;
    /** 收货人姓名 */
    selectedName?: string;
    /** 收货人电话 */
    selectedPhone?: string;
  }) || {};
  const staffId = stateData.staffId;
  const itemId = stateData.itemId;

  // 页面数据
  const [staffInfo, setStaffInfo] = useState<StaffDetail | null>(null);
  const [serviceItems, setServiceItems] = useState<StaffServiceItem[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<number>(itemId || 0);
  const [loading, setLoading] = useState(true);

  // 表单状态
  const [orderType, setOrderType] = useState<"hour" | "day">("hour");
  const [remark, setRemark] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // 地址数据
  const [addressInfo, setAddressInfo] = useState<{
    city: string; township: string; lat: string; lng: string;
    addressId?: number;   // 选中的地址ID（用于 order/create 接口）
    detail?: string;      // 详细地址
    name?: string;        // 收货人姓名
    phone?: string;       // 收货人电话
  } | null>(null);

  // 价格计算数据（来自 order/calculate 返回）
  const [priceData, setPriceData] = useState<CalculatePriceData | null>(null);
  const [calculating, setCalculating] = useState(false);
  // 防抖：避免短时间内重复调用 calculate
  const calculateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 订单数据（提交订单时来自 /api/massage/order/create 返回）
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [creatingOrder, setCreatingOrder] = useState(false);

  // 时间选择弹窗
  const [showTimePopup, setShowTimePopup] = useState(false);
  const [showTimeDesc, setShowTimeDesc] = useState("");
  // 缓存已选时间（用于切换 orderType 后弹窗复显）
  const [cachedBookTime, setCachedBookTime] = useState(0);
  const [cachedNum, setCachedNum] = useState(1);

  /** 调用 order/calculate 计算实时价格（带防抖） */
  const fetchCalculatePrice = useCallback(async () => {
    if (!staffId || !selectedItemId) return;

    // 清除之前的定时器
    if (calculateTimerRef.current) {
      clearTimeout(calculateTimerRef.current);
    }

    // 防抖 300ms
    calculateTimerRef.current = setTimeout(async () => {
      setCalculating(true);
      try {
        const data = await calculateOrderPrice({
          item_id: selectedItemId,
          coupon_id: "0",
          user_address_id: addressInfo?.addressId || "0",
          staff_id: staffId,
          travel_mode_id: "0",
          num: cachedNum || 1,
          accept_order_type: orderType === "hour" ? "1" : "2",
        });
        console.log("[确认订单] calculate 成功:", data);
        setPriceData(data);
      } catch (err) {
        console.error("[确认订单] calculate 失败:", err);
        setPriceData(null);
      } finally {
        setCalculating(false);
      }
    }, 300);
  }, [staffId, selectedItemId, orderType, cachedNum, addressInfo?.addressId]);

  /** 构建请求参数并调用 order/create（提交订单时使用） */
  const fetchCreateOrder = useCallback(async () => {
    if (!staffId || !selectedItemId) return;

    setCreatingOrder(true);
    try {
      // 默认预约时间: 当前时间 + 2小时
      const now = Math.floor(Date.now() / 1000);
      const bookTime = now + 7200;
      const bookEndTime = orderType === "hour" ? bookTime + 3600 : bookTime + 86400;

      const fullRemark = selectedTags.length > 0
        ? `${selectedTags.join("，")}${remark ? "，" + remark : ""}`
        : remark;

      const params: CreateOrderParams = {
        book_time: String(bookTime),
        book_end_time: String(bookEndTime),
        staff_id: staffId,
        pay_type: "weixin",
        item_id: selectedItemId,
        num: String(cachedNum || 1),
        coupon_id: "0",
        remark: fullRemark || "",
        user_address_id: addressInfo?.addressId ? String(addressInfo.addressId) : "0",
        travel_mode_id: "0",
        accept_order_type: orderType === "hour" ? "1" : "2",
      };

      console.log("[确认订单] 调用 order/create:", params);

      const formData = new FormData();
      formData.append("book_time", String(bookTime));
      formData.append("book_end_time", String(bookEndTime));
      formData.append("staff_id", String(staffId));
      formData.append("pay_type", "weixin");
      formData.append("item_id", String(selectedItemId));
      formData.append("num", String(cachedNum || 1));
      formData.append("coupon_id", "0");
      formData.append("remark", fullRemark || "");
      formData.append("user_address_id", addressInfo?.addressId ? String(addressInfo.addressId) : "0");
      formData.append("travel_mode_id", "0");
      formData.append("accept_order_type", orderType === "hour" ? "1" : "2");

      const res = await peiwantHttp.post<{
        status: number; msg: string; data: OrderData;
      }>("/api/massage/order/create", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.status === 200 && res.data.data?.id) {
        console.log("[确认订单] order/create 成功:", res.data.data);
        setOrderData(res.data.data);
      } else {
        console.warn("[确认订单] order/create 返回异常:", res.data.msg);
        setOrderData(null);
      }
    } catch (err) {
      console.error("[确认订单] order/create 失败:", err);
      setOrderData(null);
    } finally {
      setCreatingOrder(false);
    }
  }, [staffId, selectedItemId, orderType, selectedTags, remark, addressInfo?.addressId, cachedNum]);

  // 初始化：加载达人信息 → 自动调 order/create
  useEffect(() => {
    if (!staffId) return;

    Promise.all([
      peiwantHttp.get<{ status: number; data: any }>("/api/getStaffInfo", {
        params: { staff_id: staffId },
        _skipAuth: true,
      }),
      peiwantHttp.get<{ status: number; data: { list: StaffServiceItem[] } }>("/api/massage/staff_item", {
        params: { staff_id: staffId },
        _skipAuth: true,
      }),
    ]).then(([staffRes, itemRes]) => {
      if (staffRes.data.status === 200) {
        const raw = staffRes.data.data as any;
        setStaffInfo({
          id: raw.id,
          name: raw.name || raw.title || "",
          nickname: raw.name || raw.title || "",
          avatar: raw.thumb || "",
          coverImage: raw.imgs?.[0] || raw.thumb || "",
          images: raw.imgs || [],
          badge: raw.staff_cate_name || "",
          distance: raw.distance ? `${raw.distance}km` : "",
          city: raw.city_name || "",
          rating: raw.score || "-",
          score: raw.score || "-",
          stats: { following: 0, followers: 0, servedCount: raw.order_count || 0 },
          certifications: [],
          healthTag: null,
          introduction: raw.resume || "",
          resume: raw.resume || "",
          sex: raw.sex ?? 0,
          age: raw.age ?? 0,
          birthday: "", education: "", relationship: "", starSigns: "",
          height: 0, weight: 0, serviceLabel: [],
          workStatus: raw.work_status ?? 0,
          workTime: { start: raw.start_time || "", end: raw.end_time || "" },
          weekStr: raw.week_str || "",
          place: raw.place || "",
          serviceRadius: raw.service_radius || "",
          serviceScore: raw.service_score || "",
          phone: "", isRecommend: 0, status: 1,
          staffCateId: raw.staff_cate_id,
          staffCateName: raw.staff_cate_name || "",
          collectCount: 0, likeCount: 0,
          orderCount: raw.order_count || 0, commentCount: 0,
          auth: "", qualification: [], healthPic: [],
          longitude: "", latitude: "",
          currentLongitude: "", currentLatitude: "",
          currentAddress: "", contentAudio: "",
        });
      }
      if (itemRes.data?.status === 200) {
        setServiceItems(itemRes.data.data.list || []);
      }

      // 加载完达人信息后，自动调 order/calculate 计算价格
      setLoading(false);
    }).catch(() => setLoading(false));

    // 接收从地址选择页返回的地址数据
    if (stateData.selectedCity) {
      setAddressInfo({
        city: stateData.selectedCity,
        township: stateData.selectedTownship || "",
        lat: stateData.selectedLat || "",
        lng: stateData.selectedLng || "",
        addressId: stateData.selectedAddressId,
        detail: stateData.selectedDetail,
        name: stateData.selectedName,
        phone: stateData.selectedPhone,
      });
    }
  }, [staffId]);

  // 当基础数据就绪后，自动调用 calculate 计算价格
  useEffect(() => {
    if (!loading && staffId && selectedItemId) {
      fetchCalculatePrice();
    }
  }, [loading, staffId, selectedItemId, fetchCalculatePrice]);

  // 服务方式或项目切换时，重新计算价格
  useEffect(() => {
    if (!loading && staffId && selectedItemId) {
      fetchCalculatePrice();
    }
  }, [orderType, selectedItemId, loading, staffId, fetchCalculatePrice]);

  // 当前选中项目
  const currentItem = serviceItems.find((i) => i.id === selectedItemId);

  // 显示价格：优先使用 calculate 接口返回值，否则 fallback 到本地计算
  const displayPrice = priceData?.price || (() => {
    const price = orderType === "hour" ? currentItem?.price : currentItem?.day_order_money;
    return price && Number(price) > 0 ? Number(price).toFixed(2) : "0.00";
  })();

  /** 切换标签 */
  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  /** 打开时间选择弹窗 */
  const handleSelectTime = () => {
    setShowTimePopup(true);
  };

  /** 时间选择确认回调 */
  const handleTimeConfirm = (params: TimeConfirmParams) => {
    console.log("[时间选择] 确认:", params);
    setShowTimeDesc(params.showDesc);
    setCachedBookTime(params.book_time);
    setCachedNum(params.num);
    setShowTimePopup(false);
    // 用选中的时间/数量重新计算价格
    fetchCalculatePrice();
  };

  /** 切换服务方式时，重置时间选择并立即重新计算价格 */
  const handleOrderTypeChange = (type: "hour" | "day") => {
    if (type === orderType) return; // 避免重复点击

    // 先清除旧价格数据，让 UI 立即回退到本地 fallback 展示新类型单价
    setPriceData(null);

    setOrderType(type);
    // 重置已选时间/数量（因为小时/天的计价单位不同）
    setShowTimeDesc("");
    setCachedBookTime(0);
    setCachedNum(1);

    // 清除防抖定时器，立即发起新计算
    if (calculateTimerRef.current) {
      clearTimeout(calculateTimerRef.current);
      calculateTimerRef.current = null;
    }

    // 立即触发价格计算（无需等待防抖）
    if (staffId && selectedItemId) {
      setCalculating(true);
      calculateOrderPrice({
        item_id: selectedItemId,
        coupon_id: "0",
        user_address_id: addressInfo?.addressId || "0",
        staff_id: staffId,
        travel_mode_id: "0",
        num: 1,
        accept_order_type: type === "hour" ? "1" : "2",
      })
        .then((data) => {
          console.log("[确认订单] 切换服务方式 → calculate 成功:", data);
          setPriceData(data);
        })
        .catch((err) => {
          console.error("[确认订单] 切换服务方式 → calculate 失败:", err);
          setPriceData(null);
        })
        .finally(() => {
          setCalculating(false);
        });
    }
  };

  /** 提交订单 → order/create → cashier → 跳转支付页 */
  const handleSubmitOrder = async () => {
    setSubmitting(true);
    try {
      // 1. 构建 order/create 参数
      const now = Math.floor(Date.now() / 1000);
      const bookTime = cachedBookTime || now + 7200;
      const bookEndTime = orderType === "hour" ? bookTime + (cachedNum || 3) * 3600 : bookTime + 86400;

      const fullRemark = selectedTags.length > 0
        ? `${selectedTags.join("，")}${remark ? "，" + remark : ""}`
        : remark;

      const params: CreateOrderParams = {
        book_time: String(bookTime),
        book_end_time: String(bookEndTime),
        staff_id: staffId,
        pay_type: "weixin",
        item_id: selectedItemId,
        num: String(cachedNum || 1),
        coupon_id: "0",
        remark: fullRemark || "",
        user_address_id: addressInfo?.addressId ? String(addressInfo.addressId) : "0",
        travel_mode_id: "0",
        accept_order_type: orderType === "hour" ? "1" : "2",
      };

      console.log("[确认订单] 提交订单:", params);

      // 2. 调用 order/create 创建订单（使用 FormData，与 calculate/cashier 保持一致）
      const formData = new FormData();
      formData.append("book_time", String(bookTime));
      formData.append("book_end_time", String(bookEndTime));
      formData.append("staff_id", String(staffId));
      formData.append("pay_type", "weixin");
      formData.append("item_id", String(selectedItemId));
      formData.append("num", String(cachedNum || 1));
      formData.append("coupon_id", "0");
      formData.append("remark", fullRemark || "");
      formData.append("user_address_id", addressInfo?.addressId ? String(addressInfo.addressId) : "0");
      formData.append("travel_mode_id", "0");
      formData.append("accept_order_type", orderType === "hour" ? "1" : "2");

      const res = await peiwantHttp.post<{
        status: number; msg: string; data: OrderData;
      }>("/api/massage/order/create", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.status !== 200 || !res.data.data?.id) {
        alert(res.data?.msg || "创建订单失败，请稍后重试");
        return;
      }

      const createdOrder = res.data.data;
      console.log("[确认订单] 订单创建成功:", createdOrder);

      // 3. 尝试获取收银台（失败时不阻断，用订单自带数据兜底）
      let cashierData: any = null;
      try {
        cashierData = await getCashier({
          order_id: createdOrder.id,
          form_type: "1", // 1=服务onsite
        });
        console.log("[确认订单] 收银台返回:", cashierData);
      } catch (cashierErr) {
        console.warn("[确认订单] cashier 获取失败，使用订单数据兜底:", cashierErr);
        // 用 order/create 返回的订单数据构建默认收银台信息
        cashierData = {
          pay_mode: [{ name: "微信支付", icon: "weixin", value: "weixin", title: "微信支付", payStatus: true, number: "" }],
          order: {
            id: createdOrder.id,
            pay_price: String((createdOrder as any).pay_price || "0"),
            add_time: (createdOrder as any).add_time || 0,
            time: Date.now() / 1000,
            order_id: createdOrder.id,
          },
        };
      }

      // 4. 跳转支付页（携带收银台数据）
      navigate("/payment", {
        state: {
          orderId: createdOrder.id,
          orderNo: createdOrder.ordersn,       // 订单号（用于调用 jsapi/pay 接口）
          totalAmount: cashierData.order.pay_price,
          type: "expert",                       // 达人订单类型
          isExpertOrder: true,
          cashier: cashierData,                 // 收银台完整数据（支付方式+订单金额）
        },
      });

      // 5. 清除时间缓存（订单已创建，BLW 后端会锁定这些时段，
      //    用户返回时需要重新选择，避免看到不可选时段的残留状态）
      setCachedBookTime(0);
      setCachedNum(1);
      setShowTimeDesc("");
    } catch (err: any) {
      console.error("[确认订单] 提交失败:", err);
      // 提取具体错误信息
      const errMsg = err?.response?.data?.msg
        || err?.response?.data?.message
        || (err as Error)?.message
        || "网络异常，请稍后重试";
      alert(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  /** 行组件 */
  function FormRow({ label, children, last }: { label: string; children?: React.ReactNode; last?: boolean }) {
    return (
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 0",
        borderBottom: last ? "none" : "1px solid #f2f2f2",
      }}>
        <span style={{ fontSize: 14, color: "#333", flexShrink: 0 }}>{label}</span>
        {children}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#fff" }}>
        <div className="w-7 h-7 border-2 border-gray-200 border-t-[#FF6B35] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#fff", paddingBottom: "max(80px, calc(env(safe-area-inset-bottom, 0px) + 80px))" }}>
      {/* ====== 导航栏 ====== */}
      <header style={{
        position: "sticky", top: 0, zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "12px 16px", background: "#fff",
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
      }}>
        <button onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer"
          style={{ background: "rgba(255,255,255,0.2)", backdropFilter: "blur(8px)", border: "none", padding: 0, position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}
        >
          <i className="ri-arrow-left-line" style={{ fontSize: 20, color: "#333" }} />
        </button>
        <span style={{ fontSize: 17, fontWeight: 600, color: "#333" }}>确认订单</span>

        {/* 价格计算中指示 */}
        {calculating && (
          <span style={{ fontSize: 11, color: PRIMARY, marginLeft: 8 }}>计算中...</span>
        )}
      </header>

      {/* ====== 地址选择 ====== */}
      <div onClick={() => navigate("/address-select", {
        state: { from: "/expert-confirm", staffId, itemId },
      })}
        style={{
          margin: "12px 16px", padding: "14px 16px", cursor: "pointer",
          borderRadius: 8, background: "#fff", border: "1px solid #f0f0f0",
        }}
      >
        {addressInfo?.addressId ? (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <i className="ri-map-pin-2-fill" style={{ fontSize: 18, color: PRIMARY }} />
              <span style={{ color: "#333", fontSize: 14, fontWeight: 600 }}>
                {addressInfo.name}{addressInfo.phone ? ` ${addressInfo.phone}` : ""}
              </span>
            </div>
            <div style={{ color: "#666", fontSize: 13, marginTop: 4, lineHeight: 1.5, paddingLeft: 24 }}>
              {addressInfo.city}{addressInfo.detail ? ` ${addressInfo.detail}` : ""}
            </div>
            <div style={{ color: "#bbb", fontSize: 11, marginTop: 4, paddingLeft: 24 }}>点击更换地址</div>
          </div>
        ) : addressInfo?.city ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <i className="ri-map-pin-line" style={{ fontSize: 16, color: PRIMARY }} />
            <span style={{ color: "#333", fontSize: 14 }}>{addressInfo.city}</span>
            <span style={{ color: PRIMARY, fontSize: 13 }}>点击更换</span>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <i className="ri-map-pin-line" style={{ fontSize: 16, color: PRIMARY }} />
            <span style={{ color: PRIMARY, fontSize: 14 }}>去选择地址</span>
          </div>
        )}
      </div>

      {/* ====== 达人信息卡片 ====== */}
      {staffInfo && (
        <div style={{ margin: "0 16px", borderRadius: 8, overflow: "hidden" }}>
          {/* 头像 + 名字 + 价格 */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px" }}>
            <img src={staffInfo.avatar} alt=""
              style={{ width: 54, height: 54, borderRadius: 12, objectFit: "cover", flexShrink: 0 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#333", marginBottom: 4 }}>
                {staffInfo.nickname || staffInfo.name}
              </div>
              {/* 显示项目单价（本地 fallback） */}
              {(() => {
                if (orderType === "hour" && currentItem && Number(currentItem.price) > 0) {
                  return (
                    <div style={{ color: PRIMARY, fontSize: 13, lineHeight: 1.6 }}>
                      ¥{Number(currentItem.price).toFixed(2)} / 小时
                    </div>
                  );
                }
                if (orderType === "day" && currentItem && Number(currentItem.day_order_money) > 0) {
                  return (
                    <div style={{ color: PRIMARY, fontSize: 13, lineHeight: 1.6 }}>
                      ¥{Number(currentItem.day_order_money).toFixed(2)} / 天
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>

          {/* 分割线 + 表单项 */}
          <div style={{ padding: "0 16px" }}>
            {/* 服务方式 */}
            <FormRow label="服务方式">
              <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                {(["hour", "day"] as const).map((type) => (
                  <button key={type} onClick={() => handleOrderTypeChange(type)} style={{
                    display: "flex", alignItems: "center", gap: 5,
                    background: "none", border: "none", cursor: "pointer", padding: 0,
                  }}>
                    <span style={{
                      width: 17, height: 17, borderRadius: "50%",
                      border: orderType === type ? `2px solid ${PRIMARY}` : "1.5px solid #ccc",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {orderType === type && <span style={{ width: 9, height: 9, borderRadius: "50%", background: PRIMARY }} />}
                    </span>
                    <span style={{ fontSize: 13, color: orderType === type ? PRIMARY : "#666" }}>
                      {type === "hour" ? "小时" : "天"}
                    </span>
                  </button>
                ))}
              </div>
            </FormRow>

            {/* 项目名称 */}
            {currentItem ? (
              <FormRow label="项目">
                <span style={{ fontSize: 12, color: "#333" }}>{currentItem.name || currentItem.title}</span>
              </FormRow>
            ) : serviceItems.length > 0 ? (
              <FormRow label="陪玩">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <img src={staffInfo.avatar} alt="" style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover" }} />
                  <span style={{ fontSize: 12, color: "#333" }}>{staffInfo.nickname}</span>
                </div>
              </FormRow>
            ) : null}

            {/* 工作范围 */}
            {staffInfo.place && (
              <FormRow label="工作范围">
                <span style={{ fontSize: 13, color: "#333", textAlign: "right" }}>
                  {staffInfo.place} ({staffInfo.serviceRadius || "--"}KM以内)
                </span>
              </FormRow>
            )}

            {/* 工作日期 */}
            {staffInfo.weekStr && (() => {
              const WEEK_MAP: Record<string, string> = {
                "1": "周一", "2": "周二", "3": "周三",
                "4": "周四", "5": "周五", "6": "周六", "7": "周日", "0": "周日",
              };
              const days = staffInfo.weekStr.split(",").map((w) => w.trim()).filter(Boolean).map((w) => WEEK_MAP[w] || w);
              return (
                <FormRow label="工作日期">
                  <span style={{ fontSize: 11, color: "#999" }}>{days.join("、")}</span>
                </FormRow>
              );
            })()}

            {/* 时间段 */}
            {(staffInfo.workTime.start || staffInfo.workTime.end) && (
              <FormRow label="时间段">
                <span style={{ fontSize: 13, color: "#333" }}>
                  {staffInfo.workTime.start || "--"} - {staffInfo.workTime.end || "--"}
                </span>
              </FormRow>
            )}

            {/* 服务时间 */}
            <FormRow label="服务时间" last>
              <div onClick={handleSelectTime} style={{ display: "flex", alignItems: "center", gap: 2, cursor: "pointer" }}>
                <span style={{
                  fontSize: 13,
                  color: showTimeDesc ? "#333" : "#ccc",
                }}>
                  {showTimeDesc || "请选择服务时间"}
                </span>
                <span style={{ fontSize: 12, color: "#ccc" }}>&gt;</span>
              </div>
            </FormRow>
          </div>
        </div>
      )}

      {/* ====== 备注信息 ====== */}
      <div style={{ margin: "12px 16px 0", padding: "0 12px" }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: "#333", paddingTop: 14, paddingBottom: 10 }}>备注信息</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, paddingBottom: 10 }}>
          {QUICK_TAGS.map((tag) => (
            <button key={tag} onClick={() => toggleTag(tag)} style={{
              padding: "4px 12px", borderRadius: 4, fontSize: 13,
              border: "1px solid #e5e5e5",
              cursor: "pointer",
              background: selectedTags.includes(tag) ? `${PRIMARY}10` : "#fff",
              color: selectedTags.includes(tag) ? PRIMARY : "#666",
              transition: "all .15s",
              lineHeight: 1.6,
            }}>{tag}</button>
          ))}
        </div>
        <textarea value={remark} onChange={(e) => setRemark(e.target.value)}
          placeholder="如有其他要求，请在此输入" rows={3} maxLength={200}
          style={{
            width: "100%", border: "none", padding: "10px 12px", fontSize: 13,
            color: "#333", resize: "none", outline: "none", boxSizing: "border-box",
            fontFamily: "inherit", background: "#f7f7f7", borderRadius: 6,
          }}
        />
      </div>

      {/* 价格汇总（来自 order/calculate 返回） */}
      <div style={{ margin: "12px 16px 0", borderTop: "1px solid #f2f2f2", paddingTop: 14, paddingLeft: 16, paddingRight: 16 }}>
        {priceData ? (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 14, color: "#333" }}>服务费用</span>
              <span style={{ fontSize: 14, color: "#333" }}>¥{priceData.item_price}</span>
            </div>
            {Number(priceData.fare_price) > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: "#666" }}>
                  出行费
                  {priceData.travel_km > 0 && (
                    <span style={{ fontSize: 11, color: "#999", marginLeft: 4 }}>
                      ({priceData.fee_km}km)
                    </span>
                  )}
                </span>
                <span style={{ fontSize: 13, color: "#333" }}>¥{priceData.fare_price}</span>
              </div>
            )}
            {Number(priceData.coupon_price) > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: "#666" }}>优惠券</span>
                <span style={{ fontSize: 13, color: PRIMARY }}>-¥{priceData.coupon_price}</span>
              </div>
            )}
            {Number(priceData.discount_price) > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: "#666" }}>折扣优惠</span>
                <span style={{ fontSize: 13, color: PRIMARY }}>-¥{priceData.discount_price}</span>
              </div>
            )}
          </>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 14, color: "#333" }}>项目费用</span>
            <span style={{ fontSize: 14, color: "#333" }}>¥ {displayPrice}</span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{ fontSize: 13, color: "#999" }}>需付款：</span>
          <span style={{ fontSize: 15, fontWeight: 600, color: PRIMARY }}>¥ {displayPrice}</span>
        </div>
      </div>

      {/* 底部固定栏 */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 99,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 16px", paddingBottom: "max(10px, env(safe-area-inset-bottom))",
        background: "#fff", boxShadow: "0 -1px 6px rgba(0,0,0,0.05)",
      }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
          <span style={{ fontSize: 13, color: "#333" }}>需支付：</span>
          <span style={{ fontSize: 20, fontWeight: 700, color: PRIMARY }}>¥ {displayPrice}</span>
          <span style={{ fontSize: 11, color: "#999" }}>起</span>
        </div>
        <button
          disabled={submitting || calculating}
          onClick={handleSubmitOrder}
          style={{
            padding: "12px 40px", borderRadius: 25, fontSize: 15, fontWeight: 600,
            color: "#fff", border: "none",
            cursor: !submitting && !calculating ? "pointer" : "not-allowed",
            background: !submitting && !calculating ? PRIMARY : "#ddd",
            transition: "background .2s", opacity: submitting || calculating ? 0.7 : 1,
          }}
        >
          {calculating ? "计算中..." : submitting ? "处理中..." : "立即购买"}
        </button>
      </div>

      {/* ====== 时间选择弹窗 ====== */}
      {showTimePopup && (
        <>
          {/* 遮罩层 */}
          <div
            onClick={() => setShowTimePopup(false)}
            style={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
              background: "rgba(0,0,0,0.5)", zIndex: 999,
            }}
          />
          {/* 弹窗内容 */}
          <div style={{
            position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1000,
            animation: "slideUp .3s ease-out",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
          }}>
            <ExpertTimeComp
              staffId={staffId ?? 0}
              itemId={selectedItemId}
              chooseType={orderType === "hour" ? 1 : 2}
              minHour={3}
              cachedBookTime={cachedBookTime}
              cachedNum={cachedNum}
              onConfirm={handleTimeConfirm}
            />
          </div>
        </>
      )}
    </div>
  );
}
